import { useMemo, useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "@/components/Toast";
import { RootState } from "@/redux/store";
import { ROLE_BOM } from "@/lib/utils";
import { routes } from "@/routes/utils";
import { setCommunityData } from "@/redux/slices";
import { useGetCommunities } from "@/services";
import { MasterModuleConfig } from "./types";

const useMasterModule = ({
  queryKey,
  idKey,
  nameKey,
  useGetDataQuery,
  useToggleStatusMutation,
  isStatusUpdateApi = false,
  statusSuccessMessage = "Status Updated Successfully",
  module,
  isDeleteFn,
  isCommunityQuery,
  hideDesc,
  hideActiveSwitch,
  enableRowView,
}: MasterModuleConfig) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [communityKey, setCommunityKey] = useState(Math.random() * 2);

  const isOwner = location?.pathname?.includes("owner");
  const [globalFilter, setGlobalFilter] = useState("");
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const selectedRow = useRef<any>(null);

  const communityData = useSelector((state: RootState) => state.currentData.communityData);
  const masterDataPermission = useSelector((state: RootState) => state.currentData.masterDataPermission);
  const currentRole = useSelector((state: RootState) => state.currentData.currentRole);

  useEffect(() => {
    if (communityData) {
      setCommunityKey(Math.random() * 3);
    }
  }, [communityData]);

  const isCamBom =
    location.pathname.startsWith("/cam") ||
    ((location.pathname.startsWith("/bom") || location.pathname.startsWith("/owner")) && currentRole === ROLE_BOM);

  const hasAddPermission = useMemo(() => (isCamBom ? masterDataPermission?.add : true), [masterDataPermission, isCamBom]);
  const hasUpdatePermission = useMemo(() => (isCamBom ? masterDataPermission?.update : true), [masterDataPermission, isCamBom]);

  // Dynamic Data Query
  const { data: moduleData, isLoading: isDataLoading, refetch } = useGetDataQuery({
    communityId: communityData?.communityId,
    module: module as string,
    isCommunityQuery,
    queryParams: { enabled: !!communityData?.communityId },
  });

  // Dynamic Toggle Status Mutation
  const { mutate: toggleStatus, isPending: isUpdateStatusPending } = useToggleStatusMutation({
    onError: (error?: any) => {
      toast.error(error?.response?.data?.message || "Something went wrong");
    },
    onSuccess: () => {
      toast.success(statusSuccessMessage);
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      setOpenDeleteDialog(false);
    },
  });

  const { data: communityList, isLoading: isCommunityLoading } = useGetCommunities({});

  const onAdd = () => navigate(`${routes.add}`);

  const onCommunityChange = (e: any) => {
    const details = communityList?.data?.find((data: any) => data.communityId == e);
    dispatch(setCommunityData(details));
    setTimeout(() => refetch(), 100);
  };
  
  return {
    onAdd,
    globalFilter,
    setGlobalFilter,
    moduleData,
    isDataLoading,
    isOwner,
    hasAddPermission,
    hasUpdatePermission,
    communityList: communityList?.data || [],
    communityId: communityData?.communityId,
    onCommunityChange,
    isCommunityLoading,
    toggleStatus,
    isUpdateStatusPending,
    idKey,
    nameKey,
    selectedRow,
    module,
    isDeleteFn,
    openDeleteDialog,
    setOpenDeleteDialog,
    communityKey,
    hideDesc,
    isStatusUpdateApi,
    hideActiveSwitch,
    enableRowView,
    onRowClick: enableRowView
      ? (row: any) => navigate(`${row.original?.[idKey]}/view`)
      : undefined,
  };
};

export default useMasterModule